import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import galleryImages from "../data/gallery";
import customGallery from "../data/customGallery"; // custom image set

// === Fade-in on scroll ===
function useScrollAnimation() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting)
            entry.target.classList.add("animate-fadeUp");
        });
      },
      { threshold: 0.1 }
    );
    const elements = document.querySelectorAll(".scroll-fade");
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

// === Scroll to Top Button ===
function ScrollToTopButton() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const handleScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      className={`fixed bottom-8 right-8 bg-brand-red text-white p-3 rounded-full shadow-lg transition-all duration-300 hover:bg-red-700 ${
        visible ? "opacity-100 scale-100" : "opacity-0 scale-0"
      }`}
      aria-label="Scroll to top"
    >
      ↑
    </button>
  );
}

// === Main Home Page ===
function Home() {
  useScrollAnimation();

  // Lazy-load hero video
  useEffect(() => {
    const video = document.getElementById("heroVideo");
    if (!video) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            video.play().catch(() => {});
            video.classList.add("opacity-100", "animate-fadeIn");
            observer.unobserve(video);
          }
        });
      },
      { threshold: 0.25 }
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  return (
    <main className="pt-24">
      {/* === Hero Section === */}
      <section className="relative h-screen flex items-center justify-center text-center overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
          <video
            id="heroVideo"
            className="absolute top-0 left-0 w-full h-full object-cover opacity-0"
            muted
            loop
            playsInline
            preload="none"
          >
            <source src="/video/texanaStore.mp4" type="video/mp4" />
            <source src="/video/texanaStore.mov" type="video/quicktime" />
            Your browser does not support the video tag.
          </video>
          <div className="absolute top-0 left-0 w-full h-full bg-black/50 z-10"></div>
        </div>

        <div className="relative z-20 text-white px-6">
          <img
            src="/logo.png"
            alt="Texana Trading Logo"
            className="h-48 md:h-56 mx-auto mb-8 animate-fadeIn"
          />
          <h2 className="text-5xl md:text-6xl font-extrabold leading-tight mb-6 animate-slideUp drop-shadow-lg">
            Put A Drop Of Texas In Your Life
          </h2>
          <p className="text-2xl md:text-3xl mb-10 text-gray-200 max-w-2xl mx-auto drop-shadow-md animate-fadeIn delay-200">
            Your one-stop shop for premium and custom rustic furniture.
          </p>
          <Link
            to="/products"
            className="px-8 py-4 bg-white text-brand-red font-semibold rounded-xl shadow hover:bg-gray-100 transition animate-fadeIn delay-300"
          >
            Explore Products
          </Link>
        </div>
      </section>

      {/* === Custom Creations === */}
      <section className="py-20 bg-gray-50 text-center scroll-fade">
        <h2 className="text-4xl font-bold text-brand-red mb-4">
          Custom Creations
        </h2>
        <p className="text-gray-700 mb-12 text-lg max-w-3xl mx-auto">
          A glimpse into our one-of-a-kind builds — each custom piece designed
          around your preferred stain, leather, and style.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 max-w-6xl mx-auto px-6">
          {customGallery.slice(0, 3).map((img, i) => (
            <Link
              to="/gallery"
              key={i}
              className="relative group rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 bg-white"
            >
              <img
                src={img.src}
                alt={img.alt}
                className="w-full h-72 object-contain transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                <span className="text-white text-lg font-semibold">
                  View Gallery →
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* === Store Gallery === */}
      <section className="py-24 max-w-6xl mx-auto px-6 text-center scroll-fade">
        <h3 className="text-4xl font-bold text-brand-red mb-4">
          Our Store
        </h3>
        <p className="text-gray-700 mb-12 text-lg max-w-3xl mx-auto">
          Take a peek inside Texana Trading Co. — where rustic Texas charm meets timeless design.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
          {galleryImages.slice(0, 3).map((img, i) => (
            <Link
              key={i}
              to="/gallery"
              className="relative group rounded-2xl shadow-lg overflow-hidden bg-white hover:shadow-2xl transition-transform duration-500"
              style={{ animationDelay: `${i * 0.1}s` }}
            >
              <img
                src={img.src}
                alt={img.alt}
                className="w-full h-72 object-contain transition-transform duration-700 ease-in-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                <span className="text-white text-lg font-semibold">
                  View Gallery →
                </span>
              </div>
            </Link>
          ))}
        </div>

        <Link
          to="/gallery"
          className="mt-6 inline-block text-brand-red font-semibold hover:underline transition-colors duration-300 scroll-fade"
        >
          See More →
        </Link>
      </section>

      <ScrollToTopButton />
    </main>
  );
}

export default Home;